import React from 'react';
import styled from 'styled-components';
import { isColorDark } from 'src/dispensary-admin/customize/helpers';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MobileNavigationStyle } from 'types/graphql';

type TabBarProps = {
  backgroundColor?: string | null;
  logo?: string | null;
  navigationStyle?: MobileNavigationStyle | null;
};

const MenuIcon = (): JSX.Element => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect y='3' width='20' height='2' fill='currentColor' />
    <rect y='9' width='20' height='2' fill='currentColor' />
    <rect y='15' width='20' height='2' fill='currentColor' />
  </svg>
);

const valueOrDefault = (value: string | null | undefined, defaultValue: string): string =>
  value?.length ? value : defaultValue;

const isSolidStyle = (navigationStyle?: MobileNavigationStyle | null): boolean =>
  navigationStyle === MobileNavigationStyle.solid;

const MobileHeader = ({ backgroundColor, logo, navigationStyle }: TabBarProps): JSX.Element => {
  const flags = useFlags();
  const showRedesign = flags['ecomm.app.v2-redesign'] ?? false;
  // When the flag is off: v1 always renders a solid header (ignoring
  // navigationStyle) and v2 never renders a header background at all.
  // Behind the flag: Solid is the only style that renders a solid header
  // background — Liquid Glass floats over content and ignores
  // headerBackgroundColor, and Platform Default renders using the Liquid
  // Glass display in this preview (the admin can't know the end-user's OS,
  // so it mirrors the modern default).
  const advancedCustomizationEnabled = flags['ecomm.app.advanced-customization'] ?? false;
  const isSolid = isSolidStyle(navigationStyle);
  const isLiquidGlass = advancedCustomizationEnabled && !isSolid;
  const showsSolidBackground = !advancedCustomizationEnabled || isSolid;
  // Elevation only applies to the new Solid style
  const showsElevation = advancedCustomizationEnabled && isSolid;
  // The v2 header only ever paints headerBackgroundColor for Solid —
  // matching the v1 header's showsSolidBackground/isSolid gating above.
  const showsHeaderBackgroundColorV2 = advancedCustomizationEnabled && isSolid;

  if (showRedesign) {
    return (
      <MobileHeaderContainerV2
        $isLiquidGlass={isLiquidGlass}
        $showsElevation={showsElevation}
        backgroundColor={showsHeaderBackgroundColorV2 ? valueOrDefault(backgroundColor, '#3387E1') : undefined}
      >
        {!!logo?.length && (
          <LogoV2>
            <img src={logo} alt='Dispensary Name' />
          </LogoV2>
        )}
        <NameV2 $useLightText={showsHeaderBackgroundColorV2 && isColorDark(valueOrDefault(backgroundColor, '#3387E1'))}>
          Dispensary Name
        </NameV2>
      </MobileHeaderContainerV2>
    );
  }

  return (
    <MobileHeaderContainer
      $isLiquidGlass={isLiquidGlass}
      $showsElevation={showsElevation}
      backgroundColor={valueOrDefault(backgroundColor, '#3387E1')}
      useLightText={showsSolidBackground && isColorDark(valueOrDefault(backgroundColor, '#3387E1'))}
    >
      <MenuIcon />
      {!!logo?.length && (
        <Logo>
          <img src={logo} alt='Dispensary Name' />
        </Logo>
      )}
      {!logo?.length && <Name>Dispensary Name</Name>}
    </MobileHeaderContainer>
  );
};

const MobileHeaderContainer = styled.div<{
  $isLiquidGlass: boolean;
  $showsElevation: boolean;
  backgroundColor: string;
  useLightText: boolean;
}>`
  align-items: center;
  background-color: ${({ $isLiquidGlass, backgroundColor }) => ($isLiquidGlass ? 'transparent' : backgroundColor)};
  backdrop-filter: ${({ $isLiquidGlass }) => ($isLiquidGlass ? 'blur(20px) saturate(180%)' : 'none')};
  box-shadow: ${({ $showsElevation }) => ($showsElevation ? '0 2px 8px rgba(0, 0, 0, 0.12)' : 'none')};
  // Liquid Glass/Platform Default float over the app canvas (transparent header),
  // so invert with theme.isBackgroundDark. Solid paints its own opaque header
  // and uses useLightText against that color. See ECOMM-12580.
  color: ${({ $isLiquidGlass, useLightText, theme }) => {
    if ($isLiquidGlass) {
      return theme?.isBackgroundDark ? '#ffffff' : '#000000';
    }
    return useLightText ? '#ffffff' : '#000000';
  }};
  display: flex;
  padding: 40px 16px 10px;
  width: 100%;

  > svg {
    margin-right: 15px;
  }
`;

const MobileHeaderContainerV2 = styled.div<{
  $isLiquidGlass: boolean;
  $showsElevation: boolean;
  backgroundColor?: string;
}>`
  background-color: ${({ $isLiquidGlass, backgroundColor }) =>
    !$isLiquidGlass && backgroundColor ? backgroundColor : 'transparent'};
  box-shadow: ${({ $showsElevation }) => ($showsElevation ? '0 2px 8px rgba(0, 0, 0, 0.12)' : 'none')};
  // When no opaque headerBackgroundColor is painted (Liquid Glass/Platform
  // Default, or Solid without advanced customization) the header sits
  // directly on the app's own background, so its text needs to invert with
  // it -- NameV2 falls back to "inherit" for exactly this case. When an
  // opaque backgroundColor IS painted (Solid), leave the base ink alone --
  // NameV2's own light/dark check (against that header color, not the app
  // canvas) already decides the readable color. See ECOMM-12580.
  color: ${({ backgroundColor, theme }) => (!backgroundColor && theme?.isBackgroundDark ? '#ffffff' : 'inherit')};
  padding: 40px 16px 10px;
  width: 100%;
`;

const Name = styled.div`
  font-size: 16px;
  font-weight: bold;
`;

const NameV2 = styled.div<{ $useLightText: boolean }>`
  color: ${({ $useLightText }) => ($useLightText ? '#ffffff' : 'inherit')};
  font-size: 24px;
  font-weight: bold;
`;

const Logo = styled.div`
  flex: 1 0 0px;
  text-align: center;

  img {
    margin-left: -30px;
    max-height: 34px;
    width: auto;
  }
`;

const LogoV2 = styled.div`
  img {
    max-height: 50px;
    width: auto;
  }
`;

export default MobileHeader;
